import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Car, Wrench, CircleDot, Paintbrush, ShieldCheck, ClipboardList,
  Award, Users, SearchCheck, MessageSquare, MapPin, ArrowRight,
} from "lucide-react";
import { Reveal, SectionLabel, MaskedLine } from "../components/Reveal";
import { EditorialMarquee } from "../components/Marquee";
import { BUSINESS, IMAGES } from "../constants/business";

const services = [
  { icon: Car, title: "Collision Repair", desc: "Full structural and cosmetic repair after an accident, from frame work to final finish." },
  { icon: Wrench, title: "Auto Body Repair", desc: "Panels, fenders, doors and quarter panels restored to factory condition." },
  { icon: CircleDot, title: "Dent Repair", desc: "Door dings, creases and hail damage carefully worked out of your vehicle's body." },
  { icon: Paintbrush, title: "Scratch & Paint Repair", desc: "Scratches, chips and faded paint refinished with precise color matching." },
  { icon: ShieldCheck, title: "Bumper Repair", desc: "Cracked, scuffed or dented bumpers repaired or replaced to look like new." },
  { icon: ClipboardList, title: "Vehicle Damage Assessment", desc: "A thorough, honest evaluation of the damage so you know exactly what your vehicle needs." },
];

const whyUs = [
  { icon: Award, title: "Quality Workmanship", desc: "Every repair is finished to a standard we'd be proud to put our name on." },
  { icon: Users, title: "Professional Service", desc: "Respectful, straightforward service from the moment you reach out." },
  { icon: SearchCheck, title: "Attention to Detail", desc: "From panel gaps to paint match, the details make the repair." },
  { icon: MessageSquare, title: "Honest Communication", desc: "Clear explanations and straight answers — no surprises, no runaround." },
  { icon: MapPin, title: "Convenient Local Location", desc: "Right on Washington Blvd in Jessup — easy to reach, easy to work with." },
];

const steps = [
  { num: "01", title: "Submit Your Estimate", desc: "Tell us about your vehicle and the damage. Upload photos from your phone in minutes." },
  { num: "02", title: "We Review the Damage", desc: "Our team reviews your information and determines the next steps for your repair." },
  { num: "03", title: "Get Your Vehicle Repaired", desc: "We work to restore your vehicle and get you back on the road safely." },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  useEffect(() => {
    document.title = "Mario Auto Body & Collision Repair | Auto Body Repair in Jessup, MD";
  }, []);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <img src={IMAGES.hero} alt="Auto body technician repairing a vehicle in a dark garage" className="w-full h-[120%] object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/60" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-40 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#E60000] mb-8"
            data-testid="hero-eyebrow"
          >
            Collision & Auto Body Repair — Jessup, MD
          </motion.p>

          <h1 className="font-display uppercase leading-[0.85] tracking-tight text-[15vw] sm:text-[11vw] lg:text-[8.5rem]" data-testid="hero-headline">
            <MaskedLine delay={0.35}>Quality Auto</MaskedLine>
            <MaskedLine delay={0.5}>Body Repair</MaskedLine>
            <MaskedLine delay={0.65}><span className="text-[#E60000]">You Can Trust</span></MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base md:text-lg text-zinc-300 leading-relaxed"
            data-testid="hero-subtext"
          >
            Professional collision and auto body repair for your vehicle. We're committed to quality workmanship and getting you safely back on the road.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/estimate"
              data-testid="hero-submit-estimate-btn"
              className="bg-[#E60000] hover:bg-[#FF3333] text-white font-bold uppercase tracking-wider text-sm px-10 py-5 transition-colors inline-flex items-center justify-center gap-3"
            >
              Submit an Estimate <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-btn"
              className="border border-white/25 hover:border-white text-white uppercase tracking-wider font-semibold text-sm px-10 py-5 transition-colors inline-flex items-center justify-center"
            >
              Contact Us
            </Link>
          </motion.div>
        </div>
      </section>

      <EditorialMarquee />

      {/* SERVICES */}
      <section className="py-24 md:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <SectionLabel>What We Do</SectionLabel>
            <h2 className="font-display uppercase text-5xl md:text-7xl leading-none tracking-tight mb-4">
              Body Shop <span className="text-zinc-600">Services</span>
            </h2>
            <p className="text-zinc-400 max-w-xl mb-16">From minor dents to major collision damage, we handle the full repair — bodywork, paint and everything in between.</p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div
                  className="group relative h-full border-b border-r border-white/10 p-8 md:p-10 hover:bg-[#0A0A0A] transition-colors"
                  data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <span className="absolute top-0 left-0 w-0 h-[2px] bg-[#E60000] group-hover:w-full transition-[width] duration-500" />
                  <s.icon size={32} strokeWidth={1.5} className="text-white mb-6 group-hover:text-[#E60000] transition-colors" />
                  <h3 className="font-display text-2xl md:text-3xl uppercase tracking-wide mb-3">{s.title}</h3>
                  <p className="text-zinc-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 md:py-32 bg-[#050505] border-y border-white/10" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal className="relative">
            <div className="relative border border-white/10">
              <img src={IMAGES.technician} alt="Technician performing bodywork on a vehicle" className="w-full h-[420px] md:h-[560px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8">
                <p className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#E60000] mb-2">On the floor</p>
                <p className="font-display text-3xl uppercase">Real repairs. Real craft.</p>
              </div>
            </div>
            <div className="hidden md:block absolute -bottom-8 -right-8 w-48 h-48 border border-white/10 -z-10" />
          </Reveal>

          <div>
            <Reveal>
              <SectionLabel>Why Choose Us</SectionLabel>
              <h2 className="font-display uppercase text-5xl md:text-6xl leading-none tracking-tight mb-12">
                Done Right.<br /><span className="text-zinc-600">Every Panel.</span>
              </h2>
            </Reveal>
            <div className="flex flex-col">
              {whyUs.map((w, i) => (
                <Reveal key={w.title} delay={i * 0.07}>
                  <div className="flex gap-5 py-5 border-t border-white/10 last:border-b" data-testid={`why-us-${w.title.toLowerCase().replace(/\s+/g, "-")}`}>
                    <w.icon size={22} strokeWidth={1.5} className="text-[#E60000] shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-white mb-1">{w.title}</h3>
                      <p className="text-zinc-500 text-sm leading-relaxed">{w.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 md:py-32" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <SectionLabel>How It Works</SectionLabel>
            <h2 className="font-display uppercase text-5xl md:text-7xl leading-none tracking-tight mb-20">
              The Repair <span className="text-zinc-600">Process</span>
            </h2>
          </Reveal>

          <div className="flex flex-col">
            {steps.map((s, i) => (
              <Reveal key={s.num} delay={0.05}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center py-12 md:py-16 border-t border-white/10 last:border-b" data-testid={`process-step-${s.num}`}>
                  <span className="md:col-span-3 font-display text-7xl md:text-9xl leading-none text-white/10 select-none">{s.num}</span>
                  <h3 className="md:col-span-4 font-display text-3xl md:text-4xl uppercase tracking-wide">{s.title}</h3>
                  <p className="md:col-span-5 text-zinc-400 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 md:py-40 overflow-hidden" data-testid="home-cta-section">
        <img src={IMAGES.garage} alt="Mechanic garage" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/75" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <h2 className="font-display uppercase text-5xl md:text-7xl leading-none tracking-tight mb-6">
              Need Auto Body <span className="text-[#E60000]">Repairs?</span>
            </h2>
            <p className="text-zinc-300 max-w-xl mx-auto mb-10">
              Send us your vehicle information and damage details to get started.
            </p>
            <Link
              to="/estimate"
              data-testid="home-cta-submit-estimate-btn"
              className="inline-flex items-center gap-3 bg-[#E60000] hover:bg-[#FF3333] text-white font-bold uppercase tracking-wider text-sm px-12 py-5 transition-colors"
            >
              Submit an Estimate <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
